/**
 * RelatoriosLgpdPage — Brief 5 B4 Relatórios LGPD (Sprint Frontend Brief 5 · Tela 1).
 *
 * 5 tipos de relatório pré-formatados gerados a partir dos dados existentes
 * (LgpdRequest + Anonymization + AuditEvent). Tudo tenant-scoped (P1).
 *
 * Padrões: Opção A (capability-driven UI) · D-B defense-in-depth (gate próprio
 * da página, não confia só na rota). Gate: `lgpd-requests-management`. Tipos
 * forenses (cascade-chain, anonymization-log) chegam bloqueados quando o caller
 * não tem `audit-events-read-sensitive` (descriptor.availableToCaller=false).
 *
 * Integridade: hash SHA-256 sempre exibido; assinatura ICP-Brasil diferida
 * (P-4) → badge "pendente", nunca renderizada como assinada.
 *
 * Backend: módulo lgpd-reports (Fatia 1). Contrato: lgpd-reports.yaml.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useMemo, useState } from 'react';

import { ExportGranularSelector } from '../features/dpo/relatorios/ExportGranularSelector';
import {
  buildGenerateRequest,
  initialFormState,
  validateForm,
  type ReportFormState,
} from '../features/dpo/relatorios/report-form';
import { ReportPreviewPanel } from '../features/dpo/relatorios/ReportPreviewPanel';
import { ReportTypeTile } from '../features/dpo/relatorios/ReportTypeTile';
import { useCapability } from '../hooks/use-capability';
import { useGenerateLgpdReport, useLgpdReportTypes } from '../hooks/use-lgpd-reports';

import '../features/dpo/relatorios/relatorios-5.css';

import type { GeneratedLgpdReport, LgpdReportType } from '../features/dpo/relatorios/types';

const DEFAULT_REPORT_TYPE: LgpdReportType = 'period-summary';

export function RelatoriosLgpdPage(): JSX.Element {
  const canManage = useCapability('lgpd-requests-management');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canManage) {
    return (
      <div className="relatorios-page" style={{ padding: 32 }}>
        <CapabilityLockedBanner
          capability="lgpd-requests-management"
          tierMin="professional"
          fallbackMessage="Os relatórios LGPD são restritos ao DPO do tenant."
        />
      </div>
    );
  }

  return <RelatoriosLgpdPageInner />;
}

function RelatoriosLgpdPageInner(): JSX.Element {
  const { data: reportTypes, isLoading, isError } = useLgpdReportTypes();
  const generate = useGenerateLgpdReport();

  const [selectedType, setSelectedType] = useState<LgpdReportType>(DEFAULT_REPORT_TYPE);
  const [form, setForm] = useState<ReportFormState>(() => initialFormState(DEFAULT_REPORT_TYPE));
  const [report, setReport] = useState<GeneratedLgpdReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedDescriptor = useMemo(
    () => reportTypes.find((r) => r.id === selectedType),
    [reportTypes, selectedType],
  );

  function handleSelectType(id: LgpdReportType): void {
    setSelectedType(id);
    setForm(initialFormState(id));
    setReport(null);
    setError(null);
  }

  function handleFormChange(patch: Partial<ReportFormState>): void {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  const validationError = validateForm(selectedType, form);

  function handleGenerate(): void {
    const pending = validateForm(selectedType, form);
    if (pending !== null) {
      setError(pending);
      return;
    }
    setError(null);
    generate.mutate(buildGenerateRequest(selectedType, form), {
      onSuccess: (result) => {
        setReport(result);
      },
      onError: (err) => {
        setReport(null);
        setError(
          err.message !== ''
            ? err.message
            : 'Não foi possível gerar o relatório. Verifique suas permissões e tente novamente.',
        );
      },
    });
  }

  return (
    <div className="relatorios-page">
      <header className="rep-pagehead">
        <h1 className="rep-pagehead__title">Relatórios LGPD</h1>
        <p className="rep-pagehead__sub">
          5 tipos pré-formatados gerados sob demanda · hash SHA-256 de integridade · tipos forenses
          são restritos a perfis com acesso à auditoria sensível.
        </p>
      </header>

      <div className="rep-layout">
        <div className="rep-layout__left">
          <section className="rep-section">
            <h2 className="rep-section__title">Tipos de relatório</h2>
            {isLoading ? (
              <p className="rep-muted">Carregando tipos…</p>
            ) : isError ? (
              <p className="rep-alert rep-alert--error" role="alert">
                Falha ao carregar os tipos de relatório.
              </p>
            ) : (
              <div className="rep-tiles" role="radiogroup" aria-label="Tipos de relatório">
                {reportTypes.map((r) => (
                  <ReportTypeTile
                    key={r.id}
                    report={r}
                    selected={selectedType === r.id}
                    onSelect={handleSelectType}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="rep-section">
            <h2 className="rep-section__title">Escopo da exportação</h2>
            <ExportGranularSelector
              reportType={selectedType}
              form={form}
              onChange={handleFormChange}
            />

            <div className="rep-generate">
              <button
                type="button"
                className="rep-btn rep-btn--primary"
                disabled={validationError !== null || generate.isPending}
                onClick={handleGenerate}
              >
                {generate.isPending ? 'Gerando…' : 'Gerar relatório'}
              </button>
              {validationError !== null && (
                <span className="rep-generate__hint">{validationError}</span>
              )}
            </div>
          </section>
        </div>

        <aside className="rep-layout__right">
          <ReportPreviewPanel
            selectedDescriptor={selectedDescriptor}
            report={report}
            isGenerating={generate.isPending}
            error={error}
          />
        </aside>
      </div>
    </div>
  );
}
